import { RiAddLine, RiCloseLine } from '@remixicon/react';
import { useEffect, useState } from 'react';
import { AgButton, AgCheckbox, AgText } from 'src/components/ui';
import { useGridFilters } from '../../filters/useGridFilters';
import type { BaseGridStateWithHelpers } from '../../shared/baseGridTypes';
import ToolbarButton from '../ToolbarButton';
import type { FilterColumn } from './filterItemTypes';

type AddFilterProps = {
  columns: FilterColumn[];
  enhancedGridState: BaseGridStateWithHelpers;
  'data-testid'?: string;
  applyTestId?: string;
  checkboxTestIdPrefix?: string;
};

export default function AddFilter({
  columns,
  enhancedGridState,
  'data-testid': dataTestId,
  applyTestId,
  checkboxTestIdPrefix,
}: AddFilterProps) {
  const { filterableColumns, activeFilterFields, setFilterFields, clearFilters } = useGridFilters({
    columns,
    gridFilters: enhancedGridState.gridFilters,
    updateFilters: enhancedGridState.updateFilters,
  });

  const [filterKeys, setFilterKeys] = useState<string[]>(activeFilterFields);

  useEffect(() => {
    setFilterKeys(activeFilterFields);
  }, [activeFilterFields]);

  const toggleFilter = (field: string, checked: boolean) => {
    setFilterKeys((prev) => (checked ? [...prev, field] : prev.filter((key) => key !== field)));
  };

  return (
    <ToolbarButton label="Filters" data-testid={dataTestId}>
      {(closeMenu) => (
        <div className="min-w-[256px] flex-1 flex flex-col overflow-hidden">
          <div className="p-3 pl-4 shrink-0 border-b border-neutral-5">
            <AgText size="12" className="tracking-wider">
              Add Filters
            </AgText>
          </div>
          <div className="flex-1 overflow-y-auto min-h-0 flex flex-col">
            {filterableColumns.map((col) => (
              <AgCheckbox
                key={col.field}
                className="py-1.5 px-2 hover:bg-brand-1"
                isSelected={filterKeys.includes(col.field)}
                label={col.headerName ?? col.field}
                onChange={(val) => toggleFilter(col.field, val)}
                size="sm"
                data-testid={checkboxTestIdPrefix ? `${checkboxTestIdPrefix}-${col.field}` : undefined}
              />
            ))}
          </div>
          <div className="flex justify-between p-3 border-t border-neutral-5 shrink-0">
            <AgButton
              variant="destructive-white"
              onPress={() => {
                setFilterKeys([]);
                clearFilters();
                closeMenu();
              }}
            >
              <RiCloseLine size={14} />
              Clear All
            </AgButton>
            <AgButton
              variant="primary"
              isDisabled={filterKeys.length === 0}
              shape="rounded"
              className="px-3"
              onPress={() => {
                setFilterFields(filterKeys);
                closeMenu();
              }}
              data-testid={applyTestId}
            >
              <RiAddLine size={14} />
              Add ({filterKeys.length})
            </AgButton>
          </div>
        </div>
      )}
    </ToolbarButton>
  );
}
